import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, RefreshCwIcon, CalendarIcon, DownloadIcon, FileTextIcon, EyeIcon } from 'lucide-react';
import { Payment, PaymentStatus } from './PaymentsView';
import { formatCurrency } from '../../utils/currencyFormatter';
interface PaymentsListProps {
  payments: Payment[];
  onSelectPayment: (payment: Payment) => void;
  locale: string;
}
export function PaymentsList({
  payments,
  onSelectPayment,
  locale
}: PaymentsListProps) {
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>(payments);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    invoiceNumber: '',
    vendorName: '',
    dateFrom: '',
    dateTo: '',
    entitySite: '',
    status: '',
    searchTerm: ''
  });
  // Apply filters when they change or payments change
  useEffect(() => {
    let result = [...payments];
    // Apply search term filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(payment => payment.paymentReference.toLowerCase().includes(term) || payment.linkedInvoices.some(invoice => invoice.invoiceNumber.toLowerCase().includes(term)) || payment.linkedInvoices.some(invoice => invoice.vendorName.toLowerCase().includes(term)) || payment.externalSourceId?.toLowerCase().includes(term));
    }
    // Apply specific filters
    if (filters.invoiceNumber) {
      result = result.filter(payment => payment.linkedInvoices.some(invoice => invoice.invoiceNumber.toLowerCase().includes(filters.invoiceNumber.toLowerCase())));
    }
    if (filters.vendorName) {
      result = result.filter(payment => payment.linkedInvoices.some(invoice => invoice.vendorName.toLowerCase().includes(filters.vendorName.toLowerCase())));
    }
    if (filters.dateFrom) {
      result = result.filter(payment => new Date(payment.paymentDate) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter(payment => new Date(payment.paymentDate) <= new Date(filters.dateTo));
    }
    if (filters.entitySite) {
      result = result.filter(payment => payment.orgUnitName.toLowerCase().includes(filters.entitySite.toLowerCase()) || payment.orgUnitPath?.toLowerCase().includes(filters.entitySite.toLowerCase()));
    }
    if (filters.status) {
      result = result.filter(payment => payment.status === filters.status);
    }
    setFilteredPayments(result);
  }, [payments, filters]);
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const clearFilters = () => {
    setFilters({
      invoiceNumber: '',
      vendorName: '',
      dateFrom: '',
      dateTo: '',
      entitySite: '',
      status: '',
      searchTerm: ''
    });
  };
  const handleExportCSV = () => {
    // In a real app, this would trigger a CSV export
    alert('Exporting payments to CSV...');
  };
  const handleExportPDF = () => {
    // In a real app, this would trigger a PDF export
    alert('Exporting payments to PDF...');
  };
  // Get unique statuses for filter dropdown
  const statuses = Array.from(new Set(payments.map(payment => payment.status)));
  // Helper function to display multiple invoice numbers
  const formatInvoiceNumbers = (payment: Payment) => {
    if (payment.linkedInvoices.length === 1) {
      return payment.linkedInvoices[0].invoiceNumber;
    }
    return `${payment.linkedInvoices[0].invoiceNumber} +${payment.linkedInvoices.length - 1} more`;
  };
  // Helper function to display vendor names
  const formatVendorNames = (payment: Payment) => {
    // Check if all invoices are from the same vendor
    const uniqueVendors = new Set(payment.linkedInvoices.map(inv => inv.vendorId));
    if (uniqueVendors.size === 1) {
      return payment.linkedInvoices[0].vendorName;
    }
    return `Multiple vendors (${uniqueVendors.size})`;
  };
  return <div className="bg-white shadow rounded-lg" data-id="element-10806">
      {/* Header and Search */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10807">
        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-10808">
          Payment Records
        </h3>
        <div className="flex items-center space-x-2" data-id="element-10809">
          <div className="relative rounded-md shadow-sm" data-id="element-10810">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10811">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-10812" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search payments..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-10813" />
          </div>
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10814">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-10815" />
            Filters
          </button>
          <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10816">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-10817" />
            Reset
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {isFilterOpen && <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-10818">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-10819">
            <div className="sm:col-span-2" data-id="element-10820">
              <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-10821">
                Invoice Number
              </label>
              <input type="text" id="invoiceNumber" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.invoiceNumber} onChange={e => handleFilterChange('invoiceNumber', e.target.value)} data-id="element-10822" />
            </div>
            <div className="sm:col-span-2" data-id="element-10823">
              <label htmlFor="vendorName" className="block text-sm font-medium text-gray-700" data-id="element-10824">
                Vendor Name
              </label>
              <input type="text" id="vendorName" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.vendorName} onChange={e => handleFilterChange('vendorName', e.target.value)} data-id="element-10825" />
            </div>
            <div className="sm:col-span-2" data-id="element-10826">
              <label htmlFor="entitySite" className="block text-sm font-medium text-gray-700" data-id="element-10827">
                Organisation Unit
              </label>
              <input type="text" id="entitySite" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={filters.entitySite} onChange={e => handleFilterChange('entitySite', e.target.value)} placeholder="Search by org unit name or path" data-id="element-10828" />
            </div>
            <div className="sm:col-span-2" data-id="element-10829">
              <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-10830">
                Payment Date From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-10831">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10832">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-10833" />
                </div>
                <input type="date" id="dateFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateFrom} onChange={e => handleFilterChange('dateFrom', e.target.value)} data-id="element-10834" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-10835">
              <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-10836">
                Payment Date To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-10837">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10838">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-10839" />
                </div>
                <input type="date" id="dateTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateTo} onChange={e => handleFilterChange('dateTo', e.target.value)} data-id="element-10840" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-10841">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-10842">
                Status
              </label>
              <select id="status" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-10843">
                <option value="" data-id="element-10844">All Statuses</option>
                {statuses.map(status => <option key={status} value={status} data-id="element-10845">
                    {status}
                  </option>)}
              </select>
            </div>
          </div>
        </div>}

      {/* Export Buttons */}
      <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-2" data-id="element-10846">
        <button onClick={handleExportCSV} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10847">
          <DownloadIcon className="h-4 w-4 mr-1" data-id="element-10848" />
          Export CSV
        </button>
        <button onClick={handleExportPDF} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10849">
          <FileTextIcon className="h-4 w-4 mr-1" data-id="element-10850" />
          Export PDF
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-10851">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10852">
          <thead className="bg-gray-50" data-id="element-10853">
            <tr data-id="element-10854">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10855">
                Invoice(s)
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10856">
                Vendor
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10857">
                Payment Reference
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10858">
                Payment Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10859">
                Payment Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10860">
                Currency
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10861">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10862">
                Organisation Unit
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10863">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10864">
            {filteredPayments.length > 0 ? filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-10865">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-10866">
                    {formatInvoiceNumbers(payment)}
                    {payment.linkedInvoices.length > 1 && <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-10867">
                        {payment.linkedInvoices.length}
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10868">
                    {formatVendorNames(payment)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10869">
                    {payment.paymentReference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10870">
                    {new Date(payment.paymentDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10871">
                    {formatCurrency(payment.amount, payment.currency, locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10872">
                    {payment.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10873">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${payment.status === 'Cleared' ? 'bg-green-100 text-green-800' : payment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : payment.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10874">
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10875">
                    <div className="flex flex-col" data-id="element-10876">
                      <span className="font-medium" data-id="element-10877">{payment.orgUnitName}</span>
                      <span className="text-xs text-gray-500" data-id="element-10878">
                        {payment.orgUnitPath}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10879">
                    <button onClick={() => onSelectPayment(payment)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" data-id="element-10880">
                      <EyeIcon className="h-4 w-4 mr-1" data-id="element-10881" />
                      View
                    </button>
                  </td>
                </tr>) : <tr data-id="element-10882">
                <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-10883">
                  No payments found matching the current filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination (simplified) */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-10884">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-10885">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-10886">
            Previous
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-10887">
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-10888">
          <div data-id="element-10889">
            <p className="text-sm text-gray-700" data-id="element-10890">
              Showing <span className="font-medium" data-id="element-10891">1</span> to{' '}
              <span className="font-medium" data-id="element-10892">{filteredPayments.length}</span> of{' '}
              <span className="font-medium" data-id="element-10893">{filteredPayments.length}</span>{' '}
              results
            </p>
          </div>
          <div data-id="element-10894">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-10895">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-10896">
                <span className="sr-only" data-id="element-10897">Previous</span>
                &laquo;
              </button>
              <button aria-current="page" className="z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium" data-id="element-10898">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-10899">
                <span className="sr-only" data-id="element-10900">Next</span>
                &raquo;
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}